import React, { useState } from "react";
import axios from "axios";
import { Flag, Loader2, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { API_BASE } from "@/App";

const REASONS = [
  { key: "wrong_answer", label: "Marked answer is wrong" },
  { key: "duplicate_options", label: "Two options are the same" },
  { key: "wrong_numbers", label: "Numbers / data in question are wrong" },
  { key: "unclear_question", label: "Question is unclear or ambiguous" },
  { key: "typo", label: "Typo or formatting issue" },
  { key: "other", label: "Something else" },
];

/**
 * Reusable "Report this question" button that posts to
 * POST /api/universal-bank/report-question. Hits mcq_reports collection;
 * the backend auto-flags items after 2+ reports.
 */
const ReportQuestionButton = ({ mcqId, questionText, size = "sm", className = "" }) => {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("wrong_answer");
  const [details, setDetails] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const { toast } = useToast();

  const submit = async () => {
    setSubmitting(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API_BASE}/api/universal-bank/report-question`,
        {
          mcq_id: mcqId,
          reason,
          details: details.trim(),
          question_text: (questionText || "").slice(0, 400),
        },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      setDone(true);
      toast({ description: "Thanks — we'll review this question." });
      setTimeout(() => {
        setOpen(false);
        setDone(false);
        setDetails("");
        setReason("wrong_answer");
      }, 1200);
    } catch (err) {
      toast({
        variant: "destructive",
        description: err.response?.data?.detail || "Could not submit report. Try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Button
        variant="ghost"
        size={size}
        onClick={() => setOpen(true)}
        className={`text-slate-500 hover:text-rose-600 hover:bg-rose-50 ${className}`}
        data-testid="report-question-trigger"
      >
        <Flag className="w-4 h-4 mr-1.5" />
        Report
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="report-question-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Flag className="w-5 h-5 text-rose-600" />
              Report this question
            </DialogTitle>
            <DialogDescription>
              Help us fix wrong answers, typos or ambiguous phrasing. Our team reviews every report.
            </DialogDescription>
          </DialogHeader>

          {done ? (
            <div className="py-6 flex flex-col items-center gap-2 text-emerald-700">
              <CheckCircle2 className="w-10 h-10" />
              <p className="text-sm font-medium">Report received — thank you.</p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="space-y-2">
                <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
                  What's wrong?
                </p>
                <div className="grid grid-cols-1 gap-2">
                  {REASONS.map((r) => (
                    <button
                      key={r.key}
                      type="button"
                      onClick={() => setReason(r.key)}
                      className={`text-left text-sm px-3 py-2 rounded-md border transition-colors ${
                        reason === r.key
                          ? "border-rose-500 bg-rose-50 text-rose-900"
                          : "border-slate-200 hover:border-rose-300 hover:bg-slate-50"
                      }`}
                      data-testid={`report-reason-${r.key}`}
                    >
                      {r.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
                  Add details <span className="text-slate-400 font-normal">(optional)</span>
                </p>
                <Textarea
                  value={details}
                  onChange={(e) => setDetails(e.target.value)}
                  placeholder="e.g. The correct answer should be 2000, not 1500."
                  rows={3}
                  maxLength={600}
                  data-testid="report-details-input"
                />
                <p className="text-[11px] text-slate-400 text-right">{details.length}/600</p>
              </div>
            </div>
          )}

          {!done && (
            <DialogFooter>
              <Button
                variant="ghost"
                onClick={() => setOpen(false)}
                disabled={submitting}
                data-testid="report-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={submit}
                disabled={submitting}
                className="bg-rose-600 hover:bg-rose-700 text-white"
                data-testid="report-submit-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  "Submit report"
                )}
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ReportQuestionButton;
